import { Link } from "react-router-dom";
import { useState } from "react";
import useOrders from "../../hooks/useOrders";
import useOrderTracking from "../../hooks/useOrderTracking";

// Icons
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import EastIcon from "@mui/icons-material/East";
import WestIcon from "@mui/icons-material/West";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/Pending";
import DirectionsRunIcon from "@mui/icons-material/DirectionsRun";
import RefreshIcon from "@mui/icons-material/Refresh";
/* ================= MODAL STYLES ================= */
const overlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0,0,0,0.4)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 9999,
};

const modalStyle = {
  background: "#fff",
  padding: 20,
  width: "500px",
  maxHeight: "80vh",
  overflowY: "auto",
  borderRadius: 8,
};

/* ================= STATUS STYLES ================= */
const statusStyles = {
  pending: {
    style: {
      backgroundColor: "#f9fafb",
      color: "#374151",
      border: "1px solid #e5e7eb",
    },
    icon: <PendingIcon fontSize="small" style={{ color: "#374151" }} />,
  },
  ongoing: {
    style: {
      backgroundColor: "#eff6ff",
      color: "#1d4ed8",
      border: "1px solid #bfdbfe",
    },
    icon: <DirectionsRunIcon fontSize="small" style={{ color: "#1d4ed8" }} />,
  },
  completed: {
    style: {
      backgroundColor: "#f0fdf4",
      color: "#047857",
      border: "1px solid #bbf7d0",
    },
    icon: <CheckCircleIcon fontSize="small" style={{ color: "#047857" }} />,
  },
};
export default function OrdersDelivery() {
  /* ================= FILTER STATES ================= */
  const [orderId, setOrderId] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const perPage = 20;
  const [showFilters, setShowFilters] = useState(false);

  /* ================= TRACKING STATES ================= */
  const [trackingOpen, setTrackingOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);

  /* ================= TRACKING QUERY ================= */
  const {
    data: trackingResponse,
    isLoading: trackingLoading,
    error: trackingError,
  } = useOrderTracking(selectedOrderId, trackingOpen);

  const trackingData = trackingResponse?.data || null;

  /* ================= ORDERS LIST ================= */
  const { orders, pagination, isLoading, error } = useOrders({
    role: "admin",
    query: {
      ...(orderId && { order_id: orderId }),
      ...(status && { status }),
    },
    page,
    perPage,
  });

  const totalPages =
    pagination?.last_page || pagination?.total_pages || pagination?.total || 1;

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  /* ================= MODAL HANDLERS ================= */
  const openTrackingModal = (id) => {
    setSelectedOrderId(id);
    setTrackingOpen(true);
  };

  const closeTrackingModal = () => {
    setTrackingOpen(false);
    setSelectedOrderId(null);
  };

  /* ================= GET STATUS STYLE ================= */
  const getStatusStyle = (status) => {
    return statusStyles[status] || statusStyles.pending;
  };

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <div className="text-red-700 font-medium">Error loading orders</div>
        <div className="text-red-600 text-sm mt-1">
          {error.message || "Unknown error"}
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .card-table {
          border: 1px solid #e5e7eb;
          border-radius: 0.75rem;
          overflow: hidden;
          box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
        }
        
        .order-table {
          margin-bottom: 0;
        }
        
        .order-table thead {
          background-color: #f9fafb;
          border-bottom: 2px solid #e5e7eb;
        }
        
        .order-table th {
          padding: 0.75rem 1rem;
          font-weight: 600;
          color: #374151;
          text-transform: uppercase;
          font-size: 0.75rem;
          letter-spacing: 0.05em;
          border-top: none;
        }
        
        .order-table td {
          padding: 1rem;
          vertical-align: middle;
          border-top: 1px solid #f3f4f6;
        }
        
        .order-table tbody tr:hover {
          background-color: #f9fafb;
        }
        
        .title-header {
          border-bottom: 1px solid #e5e7eb;
          background-color: #fff;
        }
        
        .pagination .page-item.active .page-link {
          background-color: #3b82f6;
          border-color: #3b82f6;
          color: white;
        }
        
        .pagination .page-link {
          color: #4b5563;
          border: 1px solid #d1d5db;
          padding: 0.375rem 0.75rem;
          margin: 0 0.125rem;
        }
        
        .pagination .page-link:hover {
          background-color: #f3f4f6;
          border-color: #d1d5db;
        }
        
        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0.25rem 0.75rem;
          border-radius: 9999px;
          font-size: 0.75rem;
          font-weight: 500;
          border: 1px solid;
        }
        
        .action-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border-radius: 6px;
          transition: all 0.2s;
          border: 1px solid #d1d5db;
        }
        
        .action-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        
        .filter-section {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 0.5rem;
          padding: 1rem;
          margin-bottom: 1rem;
        }
        
        .stats-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 0.5rem;
          padding: 1rem;
          box-shadow: 0 1px 2px rgba(0,0,0,0.05);
        }
        
        .stats-value {
          font-size: 1.5rem;
          font-weight: 600;
          color: #1f2937;
        }
        
        .stats-label {
          font-size: 0.875rem;
          color: #6b7280;
          margin-bottom: 0.25rem;
        }
      `}</style>

      {/* ================= HEADER & FILTERS ================= */}
      <div className="container-fluid px-0">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 className="h4 fw-bold mb-1 text-gray-800">Orders Delivery</h1>
            <p className="text-muted small mb-0">
              Manage and track order deliveries
            </p>
          </div>
        </div>

        {/* ================= STATS ================= */}
        <div className="row mb-4">
          <div className="col-md-3">
            <div className="stats-card">
              <div className="stats-label">Total Orders</div>
              <div className="stats-value">{pagination?.total || 0}</div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="stats-card">
              <div className="stats-label">Current Page</div>
              <div className="stats-value">{orders.length}</div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="stats-card">
              <div className="stats-label">Page</div>
              <div className="stats-value">
                {page} of {totalPages}
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="stats-card">
              <div className="stats-label">Status Filter</div>
              <div className="stats-value text-capitalize">
                {status || "All"}
              </div>
            </div>
          </div>
        </div>

        {/* ================= TABLE ================= */}
        <div className="card card-table">
          <div className="card-body p-0">
            <div className="title-header d-flex justify-content-between align-items-center p-3">
              <h5 className="mb-0 fw-semibold text-gray-800">Order List</h5>

              <div className="d-flex gap-3">
                <div className="position-relative">
                  <SearchIcon
                    fontSize="small"
                    className="position-absolute"
                    style={{
                      left: "10px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "#9ca3af",
                    }}
                  />
                  <input
                    type="text"
                    className="form-control form-control-sm ps-4"
                    placeholder="  Search Order ID"
                    value={orderId}
                    onChange={(e) => setOrderId(e.target.value)}
                    style={{ width: "180px" }}
                  />
                </div>

                <select
                  className="form-select form-select-sm"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  style={{ width: "140px" }}
                >
                  <option value="">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="ongoing">Ongoing</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
            </div>

            <div className="table-responsive">
              <table className="table order-table">
                <thead>
                  <tr>
                    <th className="ps-4">Order ID</th>
                    <th>
                      <div className="d-flex align-items-center gap-1">
                        <CalendarMonthIcon fontSize="small" />
                        Date
                      </div>
                    </th>
                    <th>Status</th>
                    <th>
                      <div className="d-flex align-items-center gap-1">
                        Amount
                      </div>
                    </th>
                    <th className="text-center pe-4">Action</th>
                  </tr>
                </thead>

                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan={5} className="text-center py-4">
                        <div className="d-flex justify-content-center">
                          <div
                            className="spinner-border spinner-border-sm text-primary"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <span className="ms-2">Loading orders...</span>
                        </div>
                      </td>
                    </tr>
                  ) : orders.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center py-4">
                        <div className="text-muted">
                          <div className="mb-2">
                            <SearchIcon
                              style={{ fontSize: "48px", opacity: 0.3 }}
                            />
                          </div>
                          <div>No orders found</div>
                          <small className="text-muted">
                            {orderId || status
                              ? "Try changing your search criteria"
                              : "No orders available"}
                          </small>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    orders.map((order) => {
                      const statusStyle = getStatusStyle(order.status);
                      return (
                        <tr key={order.order_id}>
                          <td className="ps-4">
                            <div className="fw-semibold">#{order.order_id}</div>
                          </td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <div>
                                <div>
                                  {order.ordered_at
                                    ? new Date(
                                        order.ordered_at
                                      ).toLocaleDateString()
                                    : "-"}
                                </div>
                                <small className="text-muted">
                                  {order.ordered_at
                                    ? new Date(
                                        order.ordered_at
                                      ).toLocaleTimeString([], {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      })
                                    : ""}
                                </small>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span
                              className="status-badge"
                              style={statusStyle.style}
                            >
                              {statusStyle.icon}
                              {order.status.charAt(0).toUpperCase() +
                                order.status.slice(1)}
                            </span>
                          </td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <span className="fw-semibold">
                                {order.order_items?.[0]?.currency_symbol || ""}{" "}
                                {parseFloat(order.total_amount || 0).toFixed(2)}
                              </span>
                            </div>
                          </td>
                          <td className="text-center pe-4">
                            <div className="d-flex justify-content-center gap-2">
                              <Link
                                className="action-btn btn-outline-primary"
                                to={`/orderdeatils/${order.order_id}`}
                                title="View Details"
                              >
                                <VisibilityIcon fontSize="small" />
                              </Link>

                              <button
                                className="action-btn btn-outline-success"
                                onClick={() =>
                                  openTrackingModal(order.order_id)
                                }
                                title="Track Delivery"
                              >
                                <LocalShippingIcon fontSize="small" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* ================= PAGINATION ================= */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center p-3 border-top">
                <div className="text-muted small">
                  Showing page {page} of {totalPages} • {orders.length} orders
                </div>

                <nav>
                  <ul className="pagination mb-0">
                    <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                      <button
                        className="page-link d-flex align-items-center"
                        onClick={() => handlePageChange(1)}
                        disabled={page === 1}
                        title="First Page"
                      >
                        <FirstPageIcon fontSize="small" />
                      </button>
                    </li>
                    <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                      <button
                        className="page-link d-flex align-items-center"
                        onClick={() => handlePageChange(page - 1)}
                        disabled={page === 1}
                        title="Previous Page"
                      >
                        <WestIcon fontSize="small" />
                      </button>
                    </li>
                    {(() => {
                      const pages = [];
                      const maxVisible = 5;
                      let startPage = Math.max(
                        1,
                        page - Math.floor(maxVisible / 2)
                      );
                      let endPage = Math.min(
                        totalPages,
                        startPage + maxVisible - 1
                      );

                      if (endPage - startPage + 1 < maxVisible) {
                        startPage = Math.max(1, endPage - maxVisible + 1);
                      }

                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(
                          <li
                            key={i}
                            className={`page-item ${
                              i === page ? "active" : ""
                            }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(i)}
                            >
                              {i}
                            </button>
                          </li>
                        );
                      }
                      return pages;
                    })()}{" "}
                    <li
                      className={`page-item ${
                        page === totalPages ? "disabled" : ""
                      }`}
                    >
                      <button
                        className="page-link d-flex align-items-center"
                        onClick={() => handlePageChange(page + 1)}
                        disabled={page === totalPages}
                        title="Next Page"
                      >
                        <EastIcon fontSize="small" />
                      </button>
                    </li>
                    <li
                      className={`page-item ${
                        page === totalPages ? "disabled" : ""
                      }`}
                    >
                      <button
                        className="page-link d-flex align-items-center"
                        onClick={() => handlePageChange(totalPages)}
                        disabled={page === totalPages}
                        title="Last Page"
                      >
                        <LastPageIcon fontSize="small" />
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ================= TRACKING MODAL ================= */}
      {trackingOpen && (
        <div style={overlayStyle}>
          <div
            style={{
              ...modalStyle,
              maxWidth: "600px",
              padding: "0",
              borderRadius: "12px",
              overflow: "hidden",
            }}
          >
            {/* Modal Body */}
            <div
              className="p-4"
              style={{ maxHeight: "70vh", overflowY: "auto" }}
            >
              {trackingLoading && (
                <div className="text-center py-5">
                  <div
                    className="spinner-border text-primary mb-3"
                    style={{ width: "3rem", height: "3rem" }}
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="text-muted">
                    Fetching order tracking details...
                  </p>
                </div>
              )}

              <style>{`
  /* Order Tracking Modal - All unique class names */
  .order-tracking-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
    backdrop-filter: blur(4px);
    animation: order-tracking-fadeIn 0.2s ease;
  }

  @keyframes order-tracking-fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  .order-tracking-modal {
    background: white;
    border-radius: 16px;
    width: 90%;
    max-width: 700px;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
    animation: order-tracking-slideUp 0.3s ease;
  }

  @keyframes order-tracking-slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  /* Header */
  .order-tracking-header {
    background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
    color: white;
    padding: 20px 24px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }

  .order-tracking-header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .order-tracking-title-section {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .order-tracking-icon-wrapper {
    background: rgba(255, 255, 255, 0.2);
    padding: 10px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }

  .order-tracking-main-icon {
    font-size: 24px !important;
  }

  .order-tracking-title {
    margin: 0;
    font-size: 22px;
    font-weight: 700;
    letter-spacing: -0.01em;
  }

  .order-tracking-subtitle {
    margin: 6px 0 0 0;
    font-size: 14px;
    opacity: 0.9;
    font-weight: 400;
  }

  .order-tracking-close-btn {
    background: rgba(255, 255, 255, 0.15);
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: white;
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
  }

  .order-tracking-close-btn:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: rotate(90deg);
  }

  /* Body */
  .order-tracking-body {
    padding: 28px 30px;
    overflow-y: auto;
    flex: 1;
    background: #f8fafc;
  }

  /* Loading State */
  .order-tracking-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
  }

  .order-tracking-spinner {
    width: 50px;
    height: 50px;
    border: 4px solid #e5e7eb;
    border-top: 4px solid #2563eb;
    border-radius: 50%;
    animation: order-tracking-spin 1s linear infinite;
    margin-bottom: 16px;
  }

  @keyframes order-tracking-spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .order-tracking-loading-text {
    color: #6b7280;
    font-size: 14px;
    font-weight: 500;
  }

  /* Error State */
  .order-tracking-error {
    background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
    border: 1px solid #fca5a5;
    border-radius: 12px;
    padding: 20px;
    display: flex;
    align-items: center;
    gap: 16px;
    margin: 20px 0;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  }

  .order-tracking-error-icon {
    background: #dc2626;
    color: white;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-shadow: 0 2px 4px rgba(220, 38, 38, 0.2);
  }

  .order-tracking-error-title {
    font-weight: 700;
    color: #b91c1c;
    font-size: 15px;
    margin-bottom: 4px;
  }

  .order-tracking-error-message {
    color: #7f1d1d;
    font-size: 13px;
    opacity: 0.9;
  }

  /* Order Summary */
  .order-tracking-summary-card {
    background: white;
    border-radius: 16px;
    padding: 24px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    display: flex;
    gap: 24px;
    margin-bottom: 32px;
    border: 1px solid #e5e7eb;
  }

  .order-tracking-summary-left,
  .order-tracking-summary-right {
    flex: 1;
  }

  .order-tracking-summary-left {
    border-right: 1px solid #f1f5f9;
    padding-right: 24px;
  }

  .order-tracking-id-label {
    color: #64748b;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .order-tracking-id-label:before {
    content: "•";
    color: #2563eb;
    font-size: 16px;
  }

  .order-tracking-id-value {
    color: #0f172a;
    font-size: 32px;
    font-weight: 800;
    font-family: 'SF Mono', 'Monaco', 'Roboto Mono', monospace;
    letter-spacing: -0.5px;
  }

  .order-tracking-status-label {
    color: #64748b;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .order-tracking-status-label:before {
    content: "•";
    color: #2563eb;
    font-size: 16px;
  }

  .order-tracking-status-badge {
    display: inline-block;
    padding: 12px 24px;
    border-radius: 50px;
    font-weight: 700;
    font-size: 15px;
    text-align: center;
    min-width: 120px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border: 2px solid;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .order-tracking-status-pending {
    background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
    color: #92400e;
    border-color: #f59e0b;
  }

  .order-tracking-status-ongoing {
    color: #1e40af;
    border-color: #3b82f6;
  }

  .order-tracking-status-completed,
  .order-tracking-status-delivered {
    background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
    color: #065f46;
    border-color: #10b981;
  }

  /* Timeline */
  .order-tracking-timeline-section {
    margin-bottom: 32px;
    background: white;
    border-radius: 16px;
    padding: 24px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    border: 1px solid #e5e7eb;
  }

  .order-tracking-timeline-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 2px solid #f1f5f9;
  }

  .order-tracking-timeline-title {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    color: #0f172a;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .order-tracking-timeline-title:before {
    content: "";
    width: 6px;
    height: 24px;
    background: #2563eb;
    border-radius: 3px;
  }

  .order-tracking-updates-count {
    background: #f1f5f9;
    color: #475569;
    padding: 8px 16px;
    border-radius: 20px;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.5px;
  }

  .order-tracking-timeline {
    position: relative;
    padding-left: 24px;
  }

  .order-tracking-timeline-item {
    position: relative;
    margin-bottom: 24px;
  }

  .order-tracking-timeline-dot-wrapper {
    position: absolute;
    left: -24px;
    top: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .order-tracking-timeline-dot {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    border: 3px solid white;
    box-shadow: 0 0 0 3px;
    position: relative;
    z-index: 2;
  }

  .order-tracking-timeline-dot-pending {
    background: #f59e0b;
    box-shadow: 0 0 0 3px #fef3c7;
  }

  .order-tracking-timeline-dot-ongoing {
    background: #3b82f6;
    box-shadow: 0 0 0 3px #dbeafe;
  }

  .order-tracking-timeline-dot-completed,
  .order-tracking-timeline-dot-delivered {
    background: #10b981;
    box-shadow: 0 0 0 3px #d1fae5;
  }

  .order-tracking-timeline-line {
    width: 3px;
    background: #e2e8f0;
    flex: 1;
    margin-top: 6px;
    min-height: 50px;
    border-radius: 3px;
  }

  .order-tracking-timeline-item-last .order-tracking-timeline-line {
    display: none;
  }

  .order-tracking-timeline-content {
    margin-left: 12px;
  }

  .order-tracking-timeline-card {
    background: white;
    border-radius: 14px;
    padding: 20px;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
    border-left: 4px solid;
    transition: all 0.3s ease;
    border: 1px solid #f1f5f9;
  }

  .order-tracking-timeline-card:hover {
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
    transform: translateX(4px);
  }

  .order-tracking-timeline-card-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 12px;
  }

  .order-tracking-timeline-status-section {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .order-tracking-timeline-status {
    padding: 6px 14px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  .order-tracking-timeline-status-pending {
    background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
    color: #92400e;
    border-left-color: #f59e0b;
  }

  .order-tracking-timeline-status-ongoing {
    background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
    color: #1e40af;
    border-left-color: #3b82f6;
  }

  .order-tracking-timeline-status-completed,
  .order-tracking-timeline-status-delivered {
    background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
    color: #065f46;
    border-left-color: #10b981;
  }

  .order-tracking-timeline-latest-badge {
    background: #2563eb;
    color: white;
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.5px;
  }

  .order-tracking-timeline-time {
    text-align: right;
  }

  .order-tracking-timeline-date {
    font-size: 14px;
    font-weight: 700;
    color: #334155;
  }

  .order-tracking-timeline-hour {
    font-size: 13px;
    color: #64748b;
    margin-top: 4px;
    font-weight: 600;
  }

  .order-tracking-timeline-description {
    color: #475569;
    font-size: 15px;
    line-height: 1.6;
    margin-bottom: 8px;
    font-weight: 500;
  }

  /* Delivery Information */
  .order-tracking-delivery-info {
    background: white;
    border-radius: 16px;
    padding: 24px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    border: 1px solid #e5e7eb;
  }

  .order-tracking-delivery-title {
    margin: 0 0 20px 0;
    font-size: 18px;
    font-weight: 700;
    color: #0f172a;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .order-tracking-delivery-title:before {
    content: "";
    width: 6px;
    height: 24px;
    background: #2563eb;
    border-radius: 3px;
  }

  .order-tracking-delivery-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 20px;
  }

  .order-tracking-delivery-item {
    padding: 16px;
    background: #f8fafc;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    transition: all 0.2s;
  }

  .order-tracking-delivery-item:hover {
    background: #f1f5f9;
    border-color: #cbd5e1;
    transform: translateY(-2px);
  }

  .order-tracking-delivery-label {
    color: #64748b;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .order-tracking-delivery-label:before {
    content: "•";
    color: #2563eb;
    font-size: 16px;
  }

  .order-tracking-delivery-value {
    color: #1e293b;
    font-size: 15px;
    font-weight: 600;
    line-height: 1.5;
  }

  .order-tracking-delivery-amount {
    color: #059669;
    font-weight: 700;
    font-size: 17px;
  }

  .order-tracking-delivery-expected {
    color: #059669;
    font-weight: 700;
  }

  /* Footer */
  .order-tracking-footer {
    background: #f1f5f9;
    border-top: 1px solid #e2e8f0;
    padding: 20px 30px;
  }

  .order-tracking-footer-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .order-tracking-footer-left {
    font-size: 13px;
    color: #64748b;
    font-weight: 500;
  }

  .order-tracking-update-time {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  .order-tracking-update-time:before {
    content: "🔄";
    font-size: 14px;
  }

  .order-tracking-footer-right {
    display: flex;
    gap: 12px;
  }

  .order-tracking-btn {
    padding: 10px 28px;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    border: none;
    transition: all 0.2s;
    letter-spacing: 0.5px;
  }

  .order-tracking-btn-close {
    background: white;
    color: #475569;
    border: 2px solid #cbd5e1;
    min-width: 100px;
  }

  .order-tracking-btn-close:hover {
    background: #f8fafc;
    border-color: #94a3b8;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  /* Scrollbar */
  .order-tracking-body::-webkit-scrollbar {
    width: 8px;
  }

  .order-tracking-body::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 4px;
  }

  .order-tracking-body::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
  }

  .order-tracking-body::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }

  /* Responsive */
  @media (max-width: 768px) {
    .order-tracking-modal {
      width: 95%;
      max-height: 95vh;
    }
    
    .order-tracking-body {
      padding: 20px;
    }
    
    .order-tracking-summary-card {
      flex-direction: column;
      gap: 20px;
    }
    
    .order-tracking-summary-left {
      border-right: none;
      padding-right: 0;
      border-bottom: 1px solid #f1f5f9;
      padding-bottom: 20px;
    }
    
    .order-tracking-delivery-grid {
      grid-template-columns: 1fr;
    }
    
    .order-tracking-footer-content {
      flex-direction: column;
      gap: 16px;
      text-align: center;
    }
    
    .order-tracking-footer-left,
    .order-tracking-footer-right {
      width: 100%;
      justify-content: center;
    }
  }
`}</style>

              {/* ================= TRACKING MODAL ================= */}
              {trackingOpen && (
                <div className="order-tracking-overlay">
                  <div className="order-tracking-modal">
                    {/* Modal Header */}
                    <div className="order-tracking-header">
                      <div className="order-tracking-header-content">
                        <div className="order-tracking-title-section">
                          <div className="order-tracking-icon-wrapper">
                            <LocalShippingIcon className="order-tracking-main-icon" />
                          </div>
                          <div>
                            <h5 className="order-tracking-title">
                              Order Tracking
                            </h5>
                            <p className="order-tracking-subtitle">
                              Real-time delivery updates
                            </p>
                          </div>
                        </div>
                        <button
                          className="order-tracking-close-btn"
                          onClick={closeTrackingModal}
                        >
                          <CloseIcon />
                        </button>
                      </div>
                    </div>

                    {/* Modal Body */}
                    <div className="order-tracking-body">
                      {trackingLoading && (
                        <div className="order-tracking-loading">
                          <div className="order-tracking-spinner"></div>
                          <p className="order-tracking-loading-text">
                            Loading tracking details...
                          </p>
                        </div>
                      )}

                      {trackingError && (
                        <div className="order-tracking-error">
                          <div className="order-tracking-error-icon">
                            <CloseIcon />
                          </div>
                          <div>
                            <div className="order-tracking-error-title">
                              Tracking Unavailable
                            </div>
                            <div className="order-tracking-error-message">
                              {trackingError.message ||
                                "Unable to load tracking information"}
                            </div>
                          </div>
                        </div>
                      )}

                      {trackingData && (
                        <>
                          {/* Order Summary */}
                          <div className="order-tracking-summary">
                            <div className="order-tracking-summary-card">
                              <div className="order-tracking-summary-left">
                                <div className="order-tracking-id-wrapper">
                                  <div className="order-tracking-id-label">
                                    ORDER ID
                                  </div>
                                  <div className="order-tracking-id-value">
                                    #{trackingData.order_id}
                                  </div>
                                </div>
                              </div>
                              <div className="order-tracking-summary-right">
                                <div className="order-tracking-status-wrapper">
                                  <div className="order-tracking-status-label">
                                    CURRENT STATUS
                                  </div>
                                  <div
                                    className={`order-tracking-status-badge order-tracking-status-${trackingData.current_status}`}
                                  >
                                    {trackingData.current_status
                                      ?.charAt(0)
                                      .toUpperCase() +
                                      trackingData.current_status?.slice(1)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Delivery Timeline */}
                          <div className="order-tracking-timeline-section">
                            <div className="order-tracking-timeline-header">
                              <h6 className="order-tracking-timeline-title">
                                Delivery Timeline
                              </h6>
                              <span className="order-tracking-updates-count">
                                {trackingData.timeline?.length || 0} Updates
                              </span>
                            </div>

                            <div className="order-tracking-timeline">
                              {trackingData.timeline?.map((item, index) => {
                                const isFirst = index === 0;
                                const isLast =
                                  index === trackingData.timeline.length - 1;

                                return (
                                  <div
                                    key={item.shipping_id || index}
                                    className={`order-tracking-timeline-item ${
                                      isFirst
                                        ? "order-tracking-timeline-item-first"
                                        : ""
                                    } ${
                                      isLast
                                        ? "order-tracking-timeline-item-last"
                                        : ""
                                    }`}
                                  >
                                    <div className="order-tracking-timeline-dot-wrapper">
                                      <div
                                        className={`order-tracking-timeline-dot order-tracking-timeline-dot-${item.status}`}
                                      ></div>
                                      {!isLast && (
                                        <div className="order-tracking-timeline-line"></div>
                                      )}
                                    </div>

                                    <div className="order-tracking-timeline-content">
                                      <div className="order-tracking-timeline-card">
                                        <div className="order-tracking-timeline-card-header">
                                          <div className="order-tracking-timeline-status-section">
                                            <span
                                              className={`order-tracking-timeline-status order-tracking-timeline-status-${item.status}`}
                                            >
                                              {item.status?.toUpperCase()}
                                            </span>
                                            {isFirst && (
                                              <span className="order-tracking-timeline-latest-badge">
                                                Latest
                                              </span>
                                            )}
                                          </div>
                                          <div className="order-tracking-timeline-time">
                                            <div className="order-tracking-timeline-date">
                                              {new Date(
                                                item.created_at
                                              ).toLocaleDateString("en-US", {
                                                month: "short",
                                                day: "numeric",
                                                year: "numeric",
                                              })}
                                            </div>
                                            <div className="order-tracking-timeline-hour">
                                              {new Date(item.created_at)
                                                .toLocaleTimeString([], {
                                                  hour: "2-digit",
                                                  minute: "2-digit",
                                                  hour12: true,
                                                })
                                                .toUpperCase()}
                                            </div>
                                          </div>
                                        </div>

                                        <div className="order-tracking-timeline-description">
                                          {item.description}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          {/* Delivery Information */}
                          <div className="order-tracking-delivery-info">
                            <h6 className="order-tracking-delivery-title">
                              Delivery Information
                            </h6>
                            <div className="order-tracking-delivery-grid">
                              <div className="order-tracking-delivery-item">
                                <div className="order-tracking-delivery-label">
                                  Delivery Address
                                </div>
                                <div className="order-tracking-delivery-value">
                                  {trackingData.order?.customer_address ||
                                    "N/A"}
                                </div>
                              </div>
                              <div className="order-tracking-delivery-item">
                                <div className="order-tracking-delivery-label">
                                  Order Date
                                </div>
                                <div className="order-tracking-delivery-value">
                                  {trackingData.order?.ordered_at
                                    ? new Date(
                                        trackingData.order.ordered_at
                                      ).toLocaleDateString("en-US", {
                                        month: "long",
                                        day: "numeric",
                                        year: "numeric",
                                      })
                                    : "N/A"}
                                </div>
                              </div>
                              <div className="order-tracking-delivery-item">
                                <div className="order-tracking-delivery-label">
                                  Order Amount
                                </div>
                                <div className="order-tracking-delivery-value order-tracking-delivery-amount">
                                  LKR{" "}
                                  {parseFloat(
                                    trackingData.order?.total_amount || 0
                                  ).toFixed(2)}
                                </div>
                              </div>
                              <div className="order-tracking-delivery-item">
                                <div className="order-tracking-delivery-label">
                                  Delivery Time
                                </div>
                                <div className="order-tracking-delivery-value order-tracking-delivery-expected">
                                  {/* {trackingData.order?.completed_at
                                    ? new Date(trackingData.order.completed_at).toLocaleTimeString('en-US', {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                        hour12: true,
                                        timeZone: 'UTC'  // Add this
                                      }).toUpperCase()
                                    : 'In Progress'} */}
                                  {trackingData.order?.completed_at
                                    ? (() => {
                                        const date = new Date(
                                          trackingData.order.completed_at
                                        );
                                        const hours = date.getUTCHours();
                                        const minutes = date.getUTCMinutes();
                                        const ampm = hours >= 12 ? "PM" : "AM";
                                        const formattedHours = hours % 12 || 12;
                                        const formattedMinutes = minutes
                                          .toString()
                                          .padStart(2, "0");
                                        return `${formattedHours}:${formattedMinutes} ${ampm}`;
                                      })()
                                    : "In Progress"}
                                </div>
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Modal Footer */}
                    <div className="order-tracking-footer">
                      <div className="order-tracking-footer-content">
                        <div className="order-tracking-footer-left">
                          <span className="order-tracking-update-time">
                            Last updated:
                            {trackingData?.order?.updated_at
                              ? new Date(
                                  trackingData.order.updated_at
                                ).toLocaleString("en-US", {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                  timeZone: "UTC",
                                })
                              : new Date().toLocaleString("en-US", {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                })}
                          </span>
                        </div>
                        <div className="order-tracking-footer-right">
                          <button
                            className="order-tracking-btn order-tracking-btn-close"
                            onClick={closeTrackingModal}
                          >
                            Close
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
